import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Building2, Send, X, Loader2, FileDown, FileSpreadsheet, Paperclip,
  Calendar, TrendingUp, Percent, Coins, RefreshCw, Users, Trash2, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEuro = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;
const fmtPct = (v) => `${((v || 0) * 100).toFixed(1)} %`;
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

export default function CapexHGPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [resending, setResending] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [filterEnseigne, setFilterEnseigne] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/capex-hg/list`, { headers });
      if (r.ok) {
        const d = await r.json();
        setItems(d.items || []);
      } else {
        toast.error('Erreur de chargement des dossiers CAPEX HG');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const downloadExport = async (simId, format, label) => {
    setDownloading(`${simId}-${format}`);
    try {
      const r = await fetch(`${API}/api/simulations/${simId}/export/${format}`, { headers });
      if (!r.ok) throw new Error('fail');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setDownloading(null);
    }
  };

  const downloadAttachment = async (simId, att) => {
    setDownloading(`${simId}-${att.filename}`);
    try {
      const r = await fetch(`${API}/api/capex-hg/${simId}/attachment/${att.announcement_id}/${att.filename}`, { headers });
      if (!r.ok) throw new Error('fail');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_name || att.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch {
      toast.error('Erreur téléchargement PJ');
    } finally {
      setDownloading(null);
    }
  };

  const exportRecap = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams();
      if (filterEnseigne !== 'all') params.set('enseigne', filterEnseigne);
      if (filterMonth !== 'all') params.set('month', filterMonth);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await fetch(`${API}/api/capex-hg/export/${format}${qs}`, { headers });
      if (!r.ok) throw new Error('fail');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CAPEX_HG_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Export récapitulatif téléchargé');
    } catch {
      toast.error("Erreur lors de l'export récapitulatif");
    } finally {
      setExporting(null);
    }
  };

  const handleResend = async (simId) => {
    setResending(simId);
    try {
      const r = await fetch(`${API}/api/simulations/${simId}/send-to-hg`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ include_contract_attachments: true }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success('Dossier renvoyé aux destinataires HG');
        load();
      } else {
        toast.error(d.detail || "Échec du renvoi");
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setResending(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/api/capex-hg/${deleteTarget.id}`, { method: 'DELETE', headers });
      if (r.ok) {
        toast.success('Dossier retiré du calendrier CAPEX HG');
        setItems(prev => prev.filter(i => i.id !== deleteTarget.id));
        setDeleteTarget(null);
      } else {
        toast.error('Échec du retrait');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setDeleting(false);
    }
  };

  // Filter options from all items
  const monthOptions = [];
  const monthSeen = new Set();
  const enseigneSet = new Set();
  items.forEach(it => {
    if (it.retailer) enseigneSet.add(it.retailer);
    const date = it.sent_at ? new Date(it.sent_at) : null;
    const key = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : '0000-00';
    if (!monthSeen.has(key)) {
      monthSeen.add(key);
      monthOptions.push({ key, label: date ? date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Sans date' });
    }
  });
  monthOptions.sort((a, b) => (a.key < b.key ? 1 : -1));
  const enseigneOptions = Array.from(enseigneSet).sort();

  const monthKeyOf = (it) => {
    const date = it.sent_at ? new Date(it.sent_at) : null;
    return date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}` : '0000-00';
  };

  // Apply filters
  const q = search.trim().toLowerCase();
  const filtered = items.filter(it =>
    (filterEnseigne === 'all' || it.retailer === filterEnseigne) &&
    (filterMonth === 'all' || monthKeyOf(it) === filterMonth) &&
    (!q || [it.project_name, it.retailer, it.city].filter(Boolean).some(s => s.toLowerCase().includes(q)))
  );

  // Totals from filtered set
  const totals = filtered.reduce((acc, it) => {
    acc.count += 1;
    acc.ca += it.ca_previsionnel || 0;
    acc.cm += it.contribution_margin || 0;
    return acc;
  }, { count: 0, ca: 0, cm: 0 });

  // Group filtered by month of sent_at, most recent first
  const byMonth = {};
  filtered.forEach(it => {
    const date = it.sent_at ? new Date(it.sent_at) : null;
    const key = monthKeyOf(it);
    const label = date ? date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : 'Sans date';
    if (!byMonth[key]) byMonth[key] = { label, items: [] };
    byMonth[key].items.push(it);
  });
  const sortedMonths = Object.keys(byMonth).sort().reverse();

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="capex-hg-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Building2 className="w-6 h-6 text-indigo-600" /> CAPEX HG
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Calendrier des dossiers CAPEX envoyés à la Direction Générale
            </p>
          </div>
          <Button variant="outline" onClick={load} className="gap-2" data-testid="capex-hg-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center text-slate-400">
              <Send className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Aucun dossier envoyé à HG pour l'instant</p>
              <p className="text-sm mt-1">Les dossiers gagnés/signés envoyés à HG apparaîtront ici.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Totals strip */}
            <div className="grid grid-cols-3 gap-3" data-testid="capex-hg-totals">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Dossiers
                </p>
                <p className="text-2xl font-bold text-slate-800 tabular-nums mt-1" data-testid="total-count">{totals.count}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> CA prévisionnel cumulé
                </p>
                <p className="text-2xl font-bold text-emerald-700 tabular-nums mt-1" data-testid="total-ca">{fmtEuro(totals.ca)}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
                  <Coins className="w-3 h-3" /> Contribution Margin totale
                </p>
                <p className="text-2xl font-bold text-indigo-700 tabular-nums mt-1" data-testid="total-cm">{fmtEuro(totals.cm)}</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un dossier (projet, enseigne, ville)…"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-8 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  data-testid="capex-hg-search"
                />
                {search && (
                  <button onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="capex-hg-search-clear">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <span className="text-xs font-medium text-slate-500">Filtrer :</span>
              <select
                value={filterEnseigne}
                onChange={(e) => setFilterEnseigne(e.target.value)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="filter-enseigne"
              >
                <option value="all">Toutes les enseignes</option>
                {enseigneOptions.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 capitalize focus:outline-none focus:ring-2 focus:ring-indigo-200"
                data-testid="filter-month"
              >
                <option value="all">Tous les mois</option>
                {monthOptions.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
              {(filterEnseigne !== 'all' || filterMonth !== 'all' || search) && (
                <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500 gap-1"
                  onClick={() => { setFilterEnseigne('all'); setFilterMonth('all'); setSearch(''); }}
                  data-testid="filter-reset">
                  <X className="w-3 h-3" /> Réinitialiser
                </Button>
              )}
              <div className="flex items-center gap-2 sm:ml-auto">
                <Button variant="outline" size="sm" className="h-9 gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => exportRecap('excel')} disabled={exporting === 'excel'}
                  data-testid="export-recap-excel">
                  {exporting === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                  Excel
                </Button>
                <Button variant="outline" size="sm" className="h-9 gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => exportRecap('pdf')} disabled={exporting === 'pdf'}
                  data-testid="export-recap-pdf">
                  {exporting === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  PDF
                </Button>
              </div>
            </div>

            <Card className="border-indigo-200 bg-indigo-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                  Calendrier des envois CAPEX auprès d'HG ({filtered.length} dossier{filtered.length > 1 ? 's' : ''})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sortedMonths.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">Aucun dossier ne correspond aux filtres.</p>
                ) : (
                <div className="space-y-6">
                {sortedMonths.map(monthKey => {
                  const month = byMonth[monthKey];
                  const sorted = month.items.sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
                  return (
                    <div key={monthKey}>
                      <h3 className="font-bold text-indigo-800 text-sm mb-3 capitalize flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-indigo-500" />
                        {month.label} <Badge variant="secondary" className="text-[10px]">{sorted.length}</Badge>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {sorted.map(it => (
                          <div
                            key={it.id}
                            className="relative bg-white rounded-xl border border-indigo-100 hover:shadow-md cursor-pointer transition-shadow p-4 group"
                            onClick={() => setSelected(it)}
                            data-testid={`capex-hg-card-${it.id}`}
                          >
                            {/* Delete (X) */}
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(it); }}
                              className="absolute top-2 right-2 w-6 h-6 rounded-full bg-slate-100 hover:bg-red-100 text-slate-400 hover:text-red-600 flex items-center justify-center transition-colors"
                              title="Retirer du calendrier"
                              data-testid={`capex-hg-delete-${it.id}`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>

                            <div className="flex items-start gap-3 pr-6">
                              <div className="text-center bg-indigo-100 rounded-lg p-2 min-w-[56px]">
                                <p className="text-[10px] text-indigo-500 uppercase">
                                  {it.sent_at ? new Date(it.sent_at).toLocaleDateString('fr-FR', { month: 'short' }) : '—'}
                                </p>
                                <p className="text-lg font-bold text-indigo-800 leading-none">
                                  {it.sent_at ? new Date(it.sent_at).getDate() : '—'}
                                </p>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-slate-800 truncate" title={it.project_name}>
                                  {it.project_name}
                                </p>
                                <p className="text-[11px] text-slate-500 truncate">
                                  {[it.retailer, it.city].filter(Boolean).join(' — ')}
                                </p>
                                <p className="text-[10px] text-slate-400 mt-0.5">Envoyé le {fmtDate(it.sent_at)}</p>
                              </div>
                            </div>

                            {/* Metrics */}
                            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100">
                              <div>
                                <p className="text-[9px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
                                  <Percent className="w-2.5 h-2.5" /> Commission
                                </p>
                                <p className="text-sm font-bold text-slate-800 tabular-nums">{fmtPct(it.commission)}</p>
                              </div>
                              <div>
                                <p className="text-[9px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
                                  <TrendingUp className="w-2.5 h-2.5" /> CA prév.
                                </p>
                                <p className="text-sm font-bold text-emerald-700 tabular-nums">{fmtEuro(it.ca_previsionnel)}</p>
                              </div>
                              <div>
                                <p className="text-[9px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
                                  <Coins className="w-2.5 h-2.5" /> Contrib.
                                </p>
                                <p className="text-sm font-bold text-indigo-700 tabular-nums">{fmtEuro(it.contribution_margin)}</p>
                              </div>
                            </div>

                            {/* Footer actions */}
                            <div className="flex items-center justify-between mt-3 pt-2">
                              <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                {it.send_count > 1 && (
                                  <Badge variant="secondary" className="text-[9px]">Envoyé {it.send_count}×</Badge>
                                )}
                                {it.attachments?.length > 0 && (
                                  <span className="flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{it.attachments.length}</span>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                                onClick={(e) => { e.stopPropagation(); handleResend(it.id); }}
                                disabled={resending === it.id}
                                data-testid={`capex-hg-resend-${it.id}`}
                              >
                                {resending === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Renvoyer
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Detail modal — attachments */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="capex-hg-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-indigo-600" /> {selected.project_name}
                </DialogTitle>
                <DialogDescription>
                  {[selected.retailer, selected.city].filter(Boolean).join(' — ')} · Envoyé le {fmtDate(selected.sent_at)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Metrics recap */}
                <div className="grid grid-cols-3 gap-2 bg-slate-50 rounded-lg p-3">
                  <div>
                    <p className="text-[10px] uppercase text-slate-400">Commission</p>
                    <p className="text-sm font-bold tabular-nums">{fmtPct(selected.commission)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-400">CA prévisionnel</p>
                    <p className="text-sm font-bold text-emerald-700 tabular-nums">{fmtEuro(selected.ca_previsionnel)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-slate-400">Contribution Margin</p>
                    <p className="text-sm font-bold text-indigo-700 tabular-nums">{fmtEuro(selected.contribution_margin)}</p>
                  </div>
                </div>

                {selected.recipients?.length > 0 && (
                  <div className="text-xs text-slate-500 flex items-start gap-2">
                    <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Destinataires : {selected.recipients.join(', ')}</span>
                  </div>
                )}

                {/* Documents */}
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Documents</p>
                  <div className="space-y-2">
                    <Button
                      variant="outline" className="w-full justify-start gap-2"
                      onClick={() => downloadExport(selected.id, 'pdf', `HG_${selected.retailer}_${selected.city}`)}
                      disabled={downloading === `${selected.id}-pdf`}
                      data-testid="capex-hg-download-pdf"
                    >
                      {downloading === `${selected.id}-pdf` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4 text-red-500" />}
                      Étude CAPEX (PDF)
                    </Button>
                    <Button
                      variant="outline" className="w-full justify-start gap-2"
                      onClick={() => downloadExport(selected.id, 'excel', `HG_${selected.retailer}_${selected.city}`)}
                      disabled={downloading === `${selected.id}-excel`}
                      data-testid="capex-hg-download-excel"
                    >
                      {downloading === `${selected.id}-excel` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-emerald-600" />}
                      Étude CAPEX (Excel)
                    </Button>
                    {(selected.attachments || []).map((att, i) => (
                      <Button
                        key={i}
                        variant="outline" className="w-full justify-start gap-2"
                        onClick={() => downloadAttachment(selected.id, att)}
                        disabled={downloading === `${selected.id}-${att.filename}`}
                        data-testid={`capex-hg-download-att-${i}`}
                      >
                        {downloading === `${selected.id}-${att.filename}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4 text-slate-500" />}
                        <span className="truncate">{att.original_name || att.filename}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button
                  className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => { handleResend(selected.id); }}
                  disabled={resending === selected.id}
                  data-testid="capex-hg-modal-resend"
                >
                  {resending === selected.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Renvoyer à HG
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="capex-hg-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" /> Retirer du calendrier
            </DialogTitle>
            <DialogDescription>
              Retirer « {deleteTarget?.project_name} » du calendrier CAPEX HG ? La simulation reste intacte et accessible ailleurs.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="capex-hg-delete-confirm">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />}
              Retirer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
